import { getDateTimeFormat } from '@helpers/helper';
import NoodleAI from '@components/Icons/NoodleAI';
import UserImage from '@components/UserImage';
import * as ApiModels from '@typings/api-models';
import SecondaryActions from '@components/DesignLibrary/SecondaryActions';
import CommentContent from '@components/Message/CommentContent';
import { Fragment } from 'react';
import GeneratedByNoodleAi from '@/components/GeneratedByNoodleAi';

import Participants from './Participants';
import ContextMessage from './ContextMessage';
import s from './Message.module.scss';

type Props = {
  isAi: boolean;
  timestamp: string;
  message: Parameters<typeof CommentContent>[0]['comment'] & {
    contextType: string | null;
    contextId: string | null;
    contextTitle: string | null;
  };
  user: Pick<ApiModels.Person, 'id' | 'name'> & {
    primaryColour?: { hex: string } | null;
    image?: Pick<ApiModels.Asset, 'url'> | null;
  };
  secondaryActions?: Parameters<typeof SecondaryActions>[0];
  replies?: (Pick<ApiModels.Message, 'id'> & {
    owner: Pick<ApiModels.Person, 'id' | 'name'> & {
      primaryColour: { hex: string } | null;
      image: { url: string } | null;
    };
  })[];
  creator: Parameters<typeof CommentContent>[0]['creator'];
  compact?: boolean;
  isReplyView?: boolean;
};

const Message: React.FC<Props> = ({ user, timestamp, message, isAi, secondaryActions, replies, creator, compact, isReplyView = true }) => {

  if (message.contextType && !isReplyView) {
    return (
      <div className={s.message}>
        <ContextMessage creator={creator} message={message} replies={replies} onOpenDiscussion={() => secondaryActions?.onSelect?.('Reply')} />
      </div>
    );
  }

  const Content = compact ? 'div' : Fragment;

  return (
    <div
      className={s.container}
      style={{
        ...(compact
          ? {
            gap: 8,
          }
          : {
            gap: 16,
          }),
      }}
    >

      <div className={s.bodyWrapper}>
        <div className={s.owner}>
          <div className={s.messageImage}>
            <UserImage size={compact ? 28 : 32} name={user.name} url={user.image?.url} color={user.primaryColour?.hex || null} />
            {isAi && (
              <span className={s.aiIconContainer}>
                <NoodleAI />
              </span>
            )}
          </div>
          <div>
            <p className={`${s.name} ${compact ? 'caption-bold' : 'body-sm'}`}>{user.name}</p>
            <p className={s.timestamp}>{getDateTimeFormat(timestamp)} {message.editedAt && <span>(edited)</span>}</p>
          </div>
        </div>
        {secondaryActions && (
          <div>
            <SecondaryActions compact position="bottom-right" {...secondaryActions} />
          </div>
        )}
      </div>
      <Content
        {...(compact && {
          className: s.content,
        })}
      >
        <CommentContent comment={message} creator={creator} />
      </Content>
      {isAi && <GeneratedByNoodleAi creatorName={user.name || ''} />}
      {Boolean(replies?.length) && (
        <button className={s.replyButton} onClick={() => secondaryActions?.onSelect('Reply')}>
          <Participants replies={replies} size={12} />
        </button>
      )}
    </div>
  );
};

export default Message;
